import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { memo, useState, useCallback, useEffect } from 'react';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog, { dialogClasses } from '@mui/material/Dialog';

import { useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';
import { useEventListener } from 'src/hooks/use-event-listener';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import SearchNotFound from 'src/components/search-not-found';

import ResultItem from './result-item';
import { search } from 'src/api/search';
import { useNavData } from '../../dashboard/config-navigation';
import { getAllItems } from './utils';
import { useOrganizationContext } from 'src/hooks/use-organization-context';

// ----------------------------------------------------------------------

type SearchResult = {
  title: string;
  path: string;
  category?: string;
};

function Searchbar() {
  const theme = useTheme();
  const router = useRouter();
  const isSearchOpen = useBoolean();
  const { currentOrg } = useOrganizationContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [defaultSuggestions, setDefaultSuggestions] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  const navData = useNavData();

  useEffect(() => {
    setDefaultSuggestions(getAllItems({ data: navData }));
  }, [navData]);

  const handleClose = useCallback(() => {
    isSearchOpen.onFalse();
    setSearchQuery('');
  }, [isSearchOpen]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'k' && event.metaKey) {
      isSearchOpen.onToggle();
      setSearchQuery('');
    }
  };

  useEventListener('keydown', handleKeyDown);

  const handleClick = useCallback(
    (path: string) => {
      if (path.includes('http')) {
        window.open(path);
      } else {
        router.push(path);
      }
      handleClose();
    },
    [handleClose, router]
  );

  const handleSearch = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const query = event.target.value;
      setSearchQuery(query);

      if (query.length >= 3) {
        setLoading(true);
        try {
          if (!currentOrg) {
            setSearchResults([]);
          } else {
            const results = await search(query, currentOrg);
            setSearchResults(results.slice(0, 20));
          }
        } catch (error) {
          console.error('Error fetching search results:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setSearchResults(defaultSuggestions);
      }
    },
    [defaultSuggestions, currentOrg]
  );

  const notFound = searchQuery && !searchResults.length && !loading;

  const renderItems = () => {
    const dataToDisplay = searchQuery ? searchResults : defaultSuggestions;
    if (!dataToDisplay.length) return null;

    const data = dataToDisplay.reduce((acc: Record<string, SearchResult[]>, item: SearchResult) => {
      const group = item.category || 'Others';
      acc[group] = acc[group] || [];
      acc[group].push(item);
      return acc;
    }, {});

    return Object.keys(data)
      .sort((a, b) => -b.localeCompare(a))
      .map((group, index) => (
        <List key={group || index} disablePadding>
          {data[group].map((item) => {
            const { title, path } = item;
            const parsedTitle = typeof title === 'string' ? title : '';
            const partsTitle = parse(parsedTitle, match(parsedTitle, searchQuery) || []);

            return (
              <ResultItem
                path={[]}
                title={partsTitle}
                key={`${title}${path}`}
                groupLabel={searchQuery && group}
                onClickItem={() => handleClick(path)}
              />
            );
          })}
        </List>
      ));
  };

  const renderButton = (
    <Stack direction="row" alignItems="center">
      <IconButton onClick={isSearchOpen.onTrue}>
        <Iconify icon="eva:search-fill" />
      </IconButton>
    </Stack>
  );

  return (
    <>
      {renderButton}

      <Dialog
        fullWidth
        maxWidth="sm"
        open={isSearchOpen.value}
        onClose={handleClose}
        transitionDuration={{
          enter: theme.transitions.duration.shortest,
          exit: 0,
        }}
        PaperProps={{
          sx: {
            mt: 15,
            overflow: 'unset',
          },
        }}
        sx={{
          [`& .${dialogClasses.container}`]: {
            alignItems: 'flex-start',
          },
        }}
      >
        <Box sx={{ p: 3, borderBottom: `solid 1px ${theme.palette.divider}` }}>
          <InputBase
            fullWidth
            autoFocus
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearch}
            startAdornment={
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" width={24} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            endAdornment={<Label sx={{ letterSpacing: 1, color: 'text.secondary' }}>esc</Label>}
            inputProps={{
              sx: { typography: 'h6' },
            }}
          />
        </Box>

        <Scrollbar sx={{ p: 3, pt: 2, height: 400 }}>
          {notFound ? <SearchNotFound query={searchQuery} sx={{ py: 10 }} /> : renderItems()}
        </Scrollbar>
      </Dialog>
    </>
  );
}

export default memo(Searchbar);
